import { cn } from '@/lib/utils';
import { DAPP_TEMPLATES, useDappBuilderStore } from '@/store/dappBuilderStore';
import type { DappChain, DappFramework, DappTemplate } from '@/types/dapp-builder';
import {
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Code2,
    Globe,
    Layout,
    Loader2,
    Rocket,
    Sparkles,
    X,
} from 'lucide-react';
import { useState } from 'react';
import DappFilePreview from './DappFilePreview';
import DappVisualBuilder from './DappVisualBuilder';

/* ================================================================== */
/*  Chain & Framework Metadata                                        */
/* ================================================================== */

const CHAIN_INFO: Record<DappChain, { label: string; icon: string; color: string }> = {
  ethereum: { label: 'Ethereum', icon: '⟠', color: 'border-blue-500/40 bg-blue-500/10' },
  polygon: { label: 'Polygon', icon: '⬡', color: 'border-purple-500/40 bg-purple-500/10' },
  base: { label: 'Base', icon: '🔵', color: 'border-blue-400/40 bg-blue-400/10' },
  arbitrum: { label: 'Arbitrum', icon: '🔷', color: 'border-cyan-500/40 bg-cyan-500/10' },
  optimism: { label: 'Optimism', icon: '🔴', color: 'border-red-500/40 bg-red-500/10' },
  solana: { label: 'Solana', icon: '◎', color: 'border-green-500/40 bg-green-500/10' },
  sui: { label: 'Sui', icon: '💧', color: 'border-sky-500/40 bg-sky-500/10' },
  aptos: { label: 'Aptos', icon: '🅰️', color: 'border-teal-500/40 bg-teal-500/10' },
};

const FRAMEWORK_INFO: Record<DappFramework, { label: string; description: string }> = {
  'react-vite': { label: 'React + Vite', description: 'Fast SPA with wagmi/viem or chain SDK' },
  nextjs: { label: 'Next.js', description: 'Full-stack with RainbowKit + server actions' },
};

/* ================================================================== */
/*  Creation Mode Selection                                           */
/* ================================================================== */

type CreationMode = 'template' | 'ai' | 'visual';

function ModeSelector({ onSelect }: { onSelect: (mode: CreationMode) => void }) {
  const modes: {
    id: CreationMode;
    icon: React.ElementType;
    label: string;
    desc: string;
    color: string;
  }[] = [
    {
      id: 'template',
      icon: Code2,
      label: 'From Template',
      desc: 'Start with a pre-built dApp template',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      id: 'ai',
      icon: Sparkles,
      label: 'AI Generate',
      desc: 'Describe your dApp — AI builds it',
      color: 'from-purple-500 to-pink-500',
    },
    {
      id: 'visual',
      icon: Layout,
      label: 'Visual Builder',
      desc: 'Drag & drop components to design',
      color: 'from-orange-500 to-yellow-500',
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-[#aaa]">How do you want to create your dApp?</p>
      <div className="grid grid-cols-1 gap-3">
        {modes.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              className="flex items-center gap-3 p-4 rounded-lg border border-[#333] hover:border-[#555] hover:bg-[#252526] transition-all text-left group"
            >
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br',
                  m.color,
                )}
              >
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-white group-hover:text-white">
                  {m.label}
                </div>
                <div className="text-[10px] text-[#888]">{m.desc}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#555] ml-auto group-hover:text-[#aaa] transition-colors" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Template Picker                                                   */
/* ================================================================== */

function TemplatePicker({ onSelect }: { onSelect: (tpl: DappTemplate) => void }) {
  const [filter, setFilter] = useState<string>('all');
  const categories = ['all', 'defi', 'nft', 'dao', 'gaming', 'social', 'custom'];

  const filtered =
    filter === 'all' ? DAPP_TEMPLATES : DAPP_TEMPLATES.filter((t) => t.category === filter);

  return (
    <div className="space-y-3">
      {/* Category tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={cn(
              'px-2.5 py-1 rounded text-[10px] font-medium capitalize whitespace-nowrap transition-colors',
              filter === cat
                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                : 'text-[#888] hover:text-white hover:bg-[#333]',
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
        {filtered.map((tpl) => (
          <button
            key={tpl.id}
            onClick={() => onSelect(tpl)}
            className="flex flex-col gap-1.5 p-3 rounded-lg border border-[#333] hover:border-[#555] hover:bg-[#252526] transition-all text-left"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{tpl.icon}</span>
              <span className="text-xs font-medium text-white">{tpl.name}</span>
            </div>
            <p className="text-[9px] text-[#888] line-clamp-2">{tpl.description}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {tpl.chains.slice(0, 3).map((c) => (
                <span key={c} className="text-[8px] px-1.5 py-0.5 rounded bg-[#333] text-[#aaa]">
                  {CHAIN_INFO[c].icon} {CHAIN_INFO[c].label}
                </span>
              ))}
            </div>
            <span className="text-[8px] text-[#666] mt-0.5">{tpl.setupTime}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Configuration Step                                                */
/* ================================================================== */

function ConfigStep() {
  const { framework, chains, projectName, setFramework, toggleChain, setProjectName } =
    useDappBuilderStore();

  return (
    <div className="space-y-5">
      {/* Project name */}
      <div>
        <label className="block text-[10px] font-medium text-[#aaa] mb-1.5">Project Name</label>
        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="w-full bg-[#2a2a2a] border border-[#444] rounded-md px-3 py-2 text-xs text-white outline-none focus:border-indigo-500 transition-colors"
          placeholder="my-dapp"
        />
      </div>

      {/* Framework */}
      <div>
        <label className="block text-[10px] font-medium text-[#aaa] mb-1.5">Framework</label>
        <div className="grid grid-cols-2 gap-2">
          {(
            Object.entries(FRAMEWORK_INFO) as [
              DappFramework,
              (typeof FRAMEWORK_INFO)['react-vite'],
            ][]
          ).map(([id, info]) => (
            <button
              key={id}
              onClick={() => setFramework(id)}
              className={cn(
                'p-3 rounded-lg border text-left transition-all',
                framework === id
                  ? 'border-indigo-500/50 bg-indigo-500/10'
                  : 'border-[#333] hover:border-[#555]',
              )}
            >
              <div className="text-xs font-medium text-white">{info.label}</div>
              <div className="text-[9px] text-[#888] mt-0.5">{info.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Chains */}
      <div>
        <label className="block text-[10px] font-medium text-[#aaa] mb-1.5">Target Chains</label>
        <div className="flex flex-wrap gap-1.5">
          {(Object.entries(CHAIN_INFO) as [DappChain, (typeof CHAIN_INFO)['ethereum']][]).map(
            ([id, info]) => (
              <button
                key={id}
                onClick={() => toggleChain(id)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[10px] font-medium transition-all',
                  chains.includes(id)
                    ? `${info.color} text-white border`
                    : 'border-[#333] text-[#888] hover:text-white hover:border-[#555]',
                )}
              >
                <span>{info.icon}</span>
                <span>{info.label}</span>
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  AI Prompt Step                                                    */
/* ================================================================== */

function AiPromptStep() {
  const { aiPrompt, setAiPrompt } = useDappBuilderStore();

  const examplePrompts = [
    'Build a DEX aggregator that finds the best swap rates across Uniswap and Sushiswap',
    'Create an NFT launchpad where creators can set up collection mints with allowlist phases',
    'Build a DAO voting app where token holders can create and vote on proposals',
    'Create a Solana SPL token staking pool with tiered rewards',
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[10px] font-medium text-[#aaa] mb-1.5">
          Describe your dApp
        </label>
        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          className="w-full h-28 bg-[#2a2a2a] border border-[#444] rounded-md px-3 py-2 text-xs text-white outline-none focus:border-purple-500 transition-colors resize-none"
          placeholder="Describe what your dApp should do, what features it needs, and any specific contracts to integrate..."
        />
      </div>

      <div>
        <p className="text-[9px] text-[#666] mb-2">Try an example:</p>
        <div className="space-y-1.5">
          {examplePrompts.map((p, i) => (
            <button
              key={i}
              onClick={() => setAiPrompt(p)}
              className="w-full text-left text-[10px] text-[#888] hover:text-white p-2 rounded border border-transparent hover:border-[#333] hover:bg-[#252526] transition-all line-clamp-1"
            >
              <Sparkles className="w-3 h-3 inline mr-1.5 text-purple-400" />
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Generation Progress                                               */
/* ================================================================== */

function GenerationProgress() {
  const { isGenerating, generationError } = useDappBuilderStore();

  if (generationError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm text-red-300">Generation Failed</p>
        <p className="text-[10px] text-[#888] mt-1 max-w-xs">{generationError}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {isGenerating ? (
        <>
          <div className="relative">
            <Loader2 className="w-10 h-10 text-indigo-400 animate-spin" />
            <Sparkles className="w-4 h-4 text-purple-400 absolute -top-1 -right-1 animate-pulse" />
          </div>
          <p className="text-sm text-white mt-4">Generating your dApp...</p>
          <p className="text-[10px] text-[#888] mt-1">
            AI is creating components, contracts, and configurations
          </p>
        </>
      ) : (
        <>
          <CheckCircle2 className="w-10 h-10 text-green-400 mb-3" />
          <p className="text-sm text-green-300">dApp Generated!</p>
          <p className="text-[10px] text-[#888] mt-1">Review the files and deploy</p>
        </>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Main Wizard Component                                             */
/* ================================================================== */

export default function CreateDappWizard() {
  const {
    isOpen,
    step,
    close,
    setStep,
    selectTemplate,
    framework: _framework,
    chains,
    projectName,
    aiPrompt,
    isGenerating,
    generatedFiles,
    generationError: _generationError,
    generateFromTemplate,
    generateFromAi,
    generateFromVisual,
    reset,
    selectedTemplate,
  } = useDappBuilderStore();

  const [mode, setMode] = useState<CreationMode | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    reset();
    setMode(null);
    close();
  };

  const handleModeSelect = (m: CreationMode) => {
    setMode(m);
    if (m === 'visual') {
      setStep('design');
    } else if (m === 'ai') {
      setStep('configure');
    } else {
      setStep('choose-type');
    }
  };

  const handleTemplateSelect = (tpl: DappTemplate) => {
    selectTemplate(tpl);
    setStep('configure');
  };

  const handleNext = () => {
    if (step === 'configure') {
      if (mode === 'ai') {
        setStep('design'); // AI prompt step re-uses 'design' step naming, but shows prompt UI
      } else {
        generateFromTemplate();
      }
    } else if (step === 'design') {
      if (mode === 'ai') {
        generateFromAi();
      } else if (mode === 'visual') {
        generateFromVisual();
      }
    }
  };

  const handleBack = () => {
    if (step === 'configure') {
      if (mode === 'template') {
        setStep('choose-type');
      } else {
        setMode(null);
        setStep('choose-type');
      }
    } else if (step === 'design') {
      setStep('configure');
    } else if (step === 'preview') {
      setStep('configure');
    } else if (step === 'generate') {
      setStep('configure');
    }
  };

  const canProceed = () => {
    if (step === 'configure') return projectName.trim().length > 0 && chains.length > 0;
    if (step === 'design' && mode === 'ai') return aiPrompt.trim().length > 0;
    if (step === 'design' && mode === 'visual') return true;
    return true;
  };

  /* Determine which step title to show */
  const stepTitles: Record<string, string> = {
    'choose-type': mode === null ? 'Create dApp' : 'Choose Template',
    configure: 'Configure',
    design: mode === 'ai' ? 'Describe Your dApp' : 'Visual Builder',
    generate: 'Generating...',
    preview: 'Preview',
    deploy: 'Deploy',
  };

  /* Whether to show the wide layout (for visual builder) */
  const isWideMode = step === 'design' && mode === 'visual';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className={cn(
          'relative bg-[#1e1e1e] border border-[#333] rounded-xl shadow-2xl overflow-hidden flex flex-col',
          isWideMode ? 'w-[90vw] h-[85vh] max-w-[1400px]' : 'w-full max-w-lg max-h-[85vh]',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#333] shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
              <Rocket className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">
                {stepTitles[step] ?? 'Create dApp'}
              </h2>
              {selectedTemplate && step !== 'choose-type' && (
                <p className="text-[9px] text-[#888]">
                  {selectedTemplate.icon} {selectedTemplate.name}
                </p>
              )}
            </div>
          </div>
          <button onClick={handleClose} className="text-[#666] hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Mode selection (initial) */}
          {step === 'choose-type' && mode === null && <ModeSelector onSelect={handleModeSelect} />}

          {/* Template picker */}
          {step === 'choose-type' && mode === 'template' && (
            <TemplatePicker onSelect={handleTemplateSelect} />
          )}

          {/* Configure step */}
          {step === 'configure' && <ConfigStep />}

          {/* AI prompt step */}
          {step === 'design' && mode === 'ai' && <AiPromptStep />}

          {/* Visual builder */}
          {step === 'design' && mode === 'visual' && <DappVisualBuilder />}

          {/* Generation progress */}
          {step === 'generate' && <GenerationProgress />}

          {/* File preview */}
          {step === 'preview' && <DappFilePreview files={generatedFiles} />}
        </div>

        {/* Footer */}
        {step !== 'choose-type' || mode !== null ? (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#333] shrink-0">
            <button
              onClick={handleBack}
              disabled={isGenerating}
              className="flex items-center gap-1 text-[10px] text-[#888] hover:text-white transition-colors disabled:opacity-50"
            >
              <ArrowLeft className="w-3 h-3" />
              Back
            </button>

            {step !== 'generate' && step !== 'preview' && (
              <button
                onClick={step === 'choose-type' ? undefined : handleNext}
                disabled={!canProceed() || isGenerating}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all',
                  canProceed()
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : 'bg-[#333] text-[#666] cursor-not-allowed',
                )}
              >
                {step === 'configure' && mode !== 'ai' ? (
                  <>
                    Generate <Sparkles className="w-3 h-3" />
                  </>
                ) : step === 'design' ? (
                  <>
                    Generate <Sparkles className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="w-3 h-3" />
                  </>
                )}
              </button>
            )}

            {step === 'preview' && (
              <button
                onClick={() => setStep('deploy')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-500 text-white transition-all"
              >
                Deploy <Globe className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
